import { useEffect, useRef, useState } from "react";

/** Classes that make the Download button obvious for a moment once the file is ready. */
export const REVEAL_CLASSES = "ring-2 ring-primary ring-offset-2 rounded-md animate-pulse";
export const REVEAL_MS = 2500;

type Revealable = { scrollIntoView?: (o?: ScrollIntoViewOptions) => void };

/** Scroll the element to the middle of the screen. Returns false when the browser cannot (nothing to do). */
export function scrollToDownload(el: Revealable | null | undefined): boolean {
  if (!el || typeof el.scrollIntoView !== "function") return false;
  try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { el.scrollIntoView(); }
  return true;
}

/**
 * For the two-step "make the file, then Download" tools. When `ready` turns true (the result exists) the wrapper
 * it is attached to is scrolled into view and highlighted briefly, so the person sees where the Download is.
 * Wrap only the Download button in the returned ref's element. It takes no use and does no gating: the
 * DownloadButton inside still gates the file when it is pressed.
 */
export function useRevealDownload(ready: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const [highlight, setHighlight] = useState(false);
  useEffect(() => {
    if (!ready) { setHighlight(false); return; }
    scrollToDownload(ref.current);
    setHighlight(true);
    const t = setTimeout(() => setHighlight(false), REVEAL_MS);
    return () => clearTimeout(t);
  }, [ready]);
  return { ref, className: highlight ? REVEAL_CLASSES : "" };
}
